import { forwardRef } from "react";
import { Button, Form, Modal } from "react-bootstrap";

// A caret-less hamburger toggle for per-row action dropdowns. Used as the
// `as` component of a react-bootstrap <Dropdown.Toggle>.
export const HamburgerToggle = forwardRef<HTMLSpanElement, { onClick?: (e: any) => void }>(
  ({ onClick }, ref) => (
    <span
      ref={ref}
      role="button"
      title="Actions"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
      style={{ cursor: "pointer" }}
    >
      <i className="bi bi-list" style={{ fontSize: "1.2rem" }}></i>
    </span>
  )
);

// Endpoint responses aren't strongly typed, so normalise defensively: a
// paginated `{ items, total }` envelope or a bare array are both accepted.
export function normaliseResponse(data: any): { items: any[]; total: number } {
  if (Array.isArray(data)) return { items: data, total: data.length };
  const items = data?.items ?? data?.results ?? [];
  const total = data?.total ?? items.length;
  return { items, total };
}

// Values come back as MongoDB extended JSON, so ids/dates are wrapped objects
// ({ $oid } / { $date }) rather than plain scalars. Unwrap them defensively.
export function unwrapId(value: any): string {
  if (value == null) return "-";
  if (typeof value === "object") return value.$oid ?? value.oid ?? JSON.stringify(value);
  return String(value);
}

export function formatDate(value: any): string {
  const raw = value && typeof value === "object" ? value.$date ?? value.date : value;
  if (!raw) return "-";
  const date = new Date(raw);
  if (isNaN(date.getTime())) return String(raw);
  // Render in Central European Time regardless of the viewer's local timezone.
  return date.toLocaleString("en-GB", { timeZone: "Europe/Brussels" });
}

// A presentational modal showing a document as pretty-printed JSON in a black
// textarea. Loading/error/value are supplied by the caller.
export function JsonModal({
  show,
  onHide,
  title,
  isLoading,
  error,
  value,
}: {
  show: boolean;
  onHide: () => void;
  title: string;
  isLoading?: boolean;
  error?: unknown;
  value: any;
}) {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <p className="text-muted mb-0">Loading...</p>
        ) : error ? (
          <div className="text-danger">
            {(error as any)?.message || "An error occurred while fetching the document."}
          </div>
        ) : (
          <Form.Control
            as="textarea"
            readOnly
            value={value ? JSON.stringify(value, null, 2) : ""}
            style={{
              backgroundColor: "#000",
              color: "#0f0",
              fontFamily: "monospace",
              fontSize: "0.85em",
              minHeight: "60vh",
              whiteSpace: "pre",
            }}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
